import { useState, useRef } from 'react';
import { Snackbar, Alert as MuiAlert, Skeleton } from '@mui/material';
import { useSubscription } from './hooks/useSubscription';
import { useFcmToken } from './hooks/useFcmToken';
import { SubscribePopover } from './SubscribePopover';
import { SubscribeButton } from './SubscribeButton';

export interface EventSubscribeBellProps {
    eventId: string;
    eventTitle: string;
}

function noop() {}

export function EventSubscribeBell({ eventId, eventTitle }: EventSubscribeBellProps) {
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [snack, setSnack] = useState<string | null>(null);

    const { subscription, loading, subscribe, update, unsubscribe } = useSubscription(eventId);
    const { ensureToken } = useFcmToken();

    // Keeps the button's place while the subscription loads: the page header
    // would otherwise reflow (on a phone the title wraps to another line).
    if (loading) {
        return (
            <Skeleton variant="rounded" data-testid="subscribe-placeholder">
                <SubscribeButton subscribed={false} onClick={noop} disabled />
            </Skeleton>
        );
    }
    const isSubscribed = !!subscription;

    return (
        <>
            <SubscribeButton
                ref={anchorRef}
                subscribed={isSubscribed}
                onClick={() => setPopoverOpen(true)}
            />

            {/* Mount only when open so the popover's internal state is seeded
                fresh from the current subscription each time (useState
                initializers don't re-run on prop change). */}
            {popoverOpen && (
                <SubscribePopover
                    open={popoverOpen}
                    anchorEl={anchorRef.current}
                    onClose={() => setPopoverOpen(false)}
                    mode={isSubscribed ? 'edit' : 'create'}
                    initialAlertTypes={subscription?.alert_types ?? null}
                    initialMinSeverity={subscription?.min_severity ?? 'info'}
                    onSave={async ({ alert_types, min_severity }) => {
                        if (isSubscribed) {
                            await update({ alert_types, min_severity });
                            setSnack('Subscription updated');
                        } else {
                            await ensureToken();
                            await subscribe({ alert_types, min_severity, event_title: eventTitle });
                            setSnack(`Subscribed to '${eventTitle}'`);
                        }
                    }}
                    onUnsubscribe={isSubscribed ? async () => {
                        await unsubscribe();
                        setSnack('Unsubscribed');
                    } : undefined}
                />
            )}

            <Snackbar
                open={!!snack}
                autoHideDuration={3000}
                onClose={() => setSnack(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert severity="success" onClose={() => setSnack(null)}>{snack}</MuiAlert>
            </Snackbar>
        </>
    );
}
